import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { dataService, Event } from '../services/dataService';
import { DashboardHeader } from '../components';
import '../styles/registration-redesign.css';
import '../styles/dashboard-header.css';

interface StudentForm {
  firstName: string;
  lastName: string;
  gender: 'male' | 'female' | '';
  dateOfBirth: string;
  sports: string[]; // event ids
  ageGroup?: string; // Optional manual age group selection for 13-year-olds
}

// Grade 7 mandatory events (relay is optional)
const GRADE_7_MANDATORY_EVENTS = ['100m', '1000m', 'javelin', 'long_jump'];

const Registration: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [schoolId, setSchoolId] = useState('');
  const [students, setStudents] = useState<StudentForm[]>([{
    firstName: '',
    lastName: '',
    gender: '',
    dateOfBirth: '',
    sports: [],
    ageGroup: undefined
  }]);
  const [errors, setErrors] = useState<string[]>(['']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0); // Track which student form is currently shown
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showRegistrationRules, setShowRegistrationRules] = useState(false);
  
  const { user, createRegistration, registrations, isRegistrationOpen } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const eventsData = await dataService.getEvents();
        setEvents(eventsData);
        
        // Set school ID from user's assigned school
        if (user?.schoolId) {
          setSchoolId(user.schoolId);
        }
      } catch (error) {
        console.error('Failed to load initial data:', error);
      }
    };
    loadInitialData();
  }, [user]);

  // Helper: Calculate age as of Dec 31 this year
  const getAgeAtEndOfYear = (dob: string) => {
    if (!dob) return 0;
    const birth = new Date(dob);
    const year = new Date().getFullYear();
    const dec31 = new Date(year, 11, 31);
    let age = dec31.getFullYear() - birth.getFullYear();
    if (
      dec31.getMonth() < birth.getMonth() ||
      (dec31.getMonth() === birth.getMonth() && dec31.getDate() < birth.getDate())
    ) {
      age--;
    }
    return age;
  };

  // Helper: Get age group for student
  const getAgeGroup = (dob: string) => {
    const age = getAgeAtEndOfYear(dob);
    if (age >= 10 && age <= 12) return 'Grade 7';
    if (age >= 13 && age <= 15) return 'Under 15';
    if (age >= 16 && age <= 17) return 'Under 17';
    if (age >= 18 && age <= 20) return 'Under 20';
    return null;
  };

  // Helper: Check if student is exactly 13 years old (special case)
  const is13YearsOld = (dob: string) => {
    return getAgeAtEndOfYear(dob) === 13;
  };

  // Helper: Get effective age group for student (considering manual selection for 13-year-olds)
  const getEffectiveAgeGroup = (student: StudentForm) => {
    if (is13YearsOld(student.dateOfBirth) && student.ageGroup) {
      return student.ageGroup; // Use manual selection for 13-year-olds
    }
    return getAgeGroup(student.dateOfBirth); // Use automatic calculation for others
  };

  // Helper: Check if student is Grade 7
  const isGrade7Student = (student: StudentForm) => {
    return getEffectiveAgeGroup(student) === 'Grade 7';
  };

  // Helper: Get Grade 7 students already registered for this school
  const getGrade7StudentCount = (gender: 'male' | 'female') => {
    let count = 0;
    
    // Count from current form - count unique students
    const uniqueStudentsInForm = new Set();
    students.forEach(s => {
      if (isGrade7Student(s) && s.gender === gender) {
        const studentKey = `${s.firstName}_${s.lastName}_${s.dateOfBirth}`;
        uniqueStudentsInForm.add(studentKey);
      }
    });
    count += uniqueStudentsInForm.size;
    
    // Count from existing registrations - count unique students
    if (schoolId && registrations) {
      const uniqueStudentsInDB = new Set();
      registrations.filter(r => 
        r.schoolId === schoolId && 
        r.age_group === 'Grade 7' && 
        r.gender === gender &&
        (r.status === 'approved' || r.status === 'pending')
      ).forEach(r => {
        const studentKey = `${r.first_name}_${r.last_name}_${r.date_of_birth}`;
        uniqueStudentsInDB.add(studentKey);
      });
      count += uniqueStudentsInDB.size;
    }
    
    return count;
  };

  // Helper: Get Grade 7 students for mandatory events only (not relay)
  const getGrade7MandatoryStudentCount = (gender: 'male' | 'female') => {
    let count = 0;
    
    // Count from current form - unique students with mandatory events
    const uniqueStudentsInForm = new Set();
    students.forEach(s => {
      if (isGrade7Student(s) && s.gender === gender) {
        // Check if student has any mandatory events
        const hasMandatory = GRADE_7_MANDATORY_EVENTS.some(eventId => s.sports.includes(eventId));
        if (hasMandatory) {
          const studentKey = `${s.firstName}_${s.lastName}_${s.dateOfBirth}`;
          uniqueStudentsInForm.add(studentKey);
        }
      }
    });
    count += uniqueStudentsInForm.size;
    
    // Count from existing registrations for mandatory events - unique students
    if (schoolId && registrations) {
      const uniqueStudentsInDB = new Set();
      registrations.filter(r => 
        r.schoolId === schoolId && 
        r.age_group === 'Grade 7' && 
        r.gender === gender &&
        (r.status === 'approved' || r.status === 'pending') &&
        r.eventId && GRADE_7_MANDATORY_EVENTS.includes(r.eventId)
      ).forEach(r => {
        const studentKey = `${r.first_name}_${r.last_name}_${r.date_of_birth}`;
        uniqueStudentsInDB.add(studentKey);
      });
      count += uniqueStudentsInDB.size;
    }
    
    return count;
  };

  // Helper: Get missing mandatory events for Grade 7 student (only those still available)
  const getMissingMandatoryEvents = (student: StudentForm) => {
    if (!isGrade7Student(student)) return [];
    
    const ageGroup = getEffectiveAgeGroup(student);
    
    // Only check for mandatory events that still have spots available for this gender
    const availableMandatory = GRADE_7_MANDATORY_EVENTS.filter(eventId => {
      const currentCount = getEventStudentCount(eventId, student.gender, undefined, ageGroup);
      return currentCount < 6;
    });
    
    return availableMandatory.filter(eventId => !student.sports.includes(eventId));
  };

  // Helper: Validate Grade 7 specific rules
  const validateGrade7Rules = () => {
    const errors = [];
    
    // Check participant limits per gender - allow 6 for mandatory + 6 for relay = 12 total
    const grade7Boys = getGrade7StudentCount('male');
    const grade7Girls = getGrade7StudentCount('female');
    
    if (grade7Boys > 12) { // 6 mandatory + 6 relay
      errors.push(`Too many Grade 7 boys: ${grade7Boys}/12 maximum allowed per school (6 for mandatory events + 6 for relay).`);
    }
    
    if (grade7Girls > 12) { // 6 mandatory + 6 relay  
      errors.push(`Too many Grade 7 girls: ${grade7Girls}/12 maximum allowed per school (6 for mandatory events + 6 for relay).`);
    }
    
    // Check mandatory events limits separately
    const mandatoryBoys = getGrade7MandatoryStudentCount('male');
    const mandatoryGirls = getGrade7MandatoryStudentCount('female');
    
    if (mandatoryBoys > 6) {
      errors.push(`Too many Grade 7 boys for mandatory events: ${mandatoryBoys}/6 maximum allowed.`);
    }
    
    if (mandatoryGirls > 6) {
      errors.push(`Too many Grade 7 girls for mandatory events: ${mandatoryGirls}/6 maximum allowed.`);
    }
    
    // Check mandatory events for each Grade 7 student
    students.forEach((student, idx) => {
      if (isGrade7Student(student) && student.sports.length > 0) {
        const missing = getMissingMandatoryEvents(student);
        if (missing.length > 0) {
          const eventNames = missing.map(id => events.find(e => e.id === id)?.name || id).join(', ');
          errors.push(`Student ${idx + 1} (${student.firstName} ${student.lastName}) - Grade 7 must participate in ALL mandatory events. Missing: ${eventNames}`);
        }
      }
    });
    
    return errors;
  };

  // Helper: Check if student form is complete and valid (optimized for performance)
  const isStudentComplete = (student: StudentForm) => {
    if (!student.firstName?.trim() || !student.lastName?.trim() || !student.gender || !student.dateOfBirth) {
      return false;
    }
    
    // For 13-year-olds, age group must be manually selected
    if (is13YearsOld(student.dateOfBirth) && !student.ageGroup) {
      return false;
    }
    
    const ageGroup = getEffectiveAgeGroup(student);
    if (!ageGroup) return false;
    
    if (student.sports.length === 0) return false;
    
    // For Grade 7, check if they have mandatory events (performance optimized)
    if (isGrade7Student(student)) {
      const availableMandatory = GRADE_7_MANDATORY_EVENTS.filter(eventId => {
        const currentCount = getEventStudentCount(eventId, student.gender, undefined, ageGroup);
        return currentCount < 6;
      });
      
      // Must have all available mandatory events
      return availableMandatory.every(eventId => student.sports.includes(eventId));
    }
    
    return true;
  };

  // Helper: Get eligible sports for a student
  const getEligibleSports = (student: StudentForm) => {
    const ageGroup = getEffectiveAgeGroup(student);
    if (!ageGroup || !student.gender) return [];
    
    // Filter events based on age group
    return events.filter(event => {
      // For Grade 7 students, only show mandatory events + optional relay
      if (ageGroup === 'Grade 7') {
        // Check if this is a mandatory event (not relay)
        if (GRADE_7_MANDATORY_EVENTS.includes(event.id)) {
          // For mandatory events, only show if there are still spots available for this gender
          const currentIndex = students.findIndex(s => s === student);
          const currentCount = getEventStudentCount(event.id, student.gender, currentIndex, ageGroup);
          return currentCount < 6;
        }
        
        // Always show relay as it can have separate team
        if (event.id === '4x100_relay') {
          return true;
        }
        
        return false;
      }
      
      // For Under 15 students, show specific events (NO 1000m)
      if (ageGroup === 'Under 15') {
        const under15Events = [
          '100m', '200m', '400m', '800m', '1500m', 
          'high_jump', 'long_jump', 'shot_put', 'javelin', 'discus', 
          '4x100_relay', '4x400_relay'
        ];
        return under15Events.includes(event.id) && event.id !== '1000m';
      }
      
      // For Under 17 students
      if (ageGroup === 'Under 17') {
        // Boys can participate in all 15 events
        if (student.gender === 'male') {
          return true; // All events available
        }
        
        // Girls can participate in all events EXCEPT 3000m
        if (student.gender === 'female') {
          return event.id !== '3000m';
        }
      }
      
      // For Under 20 students
      if (ageGroup === 'Under 20') {
        // Both boys and girls can participate in all 15 events
        return true; // All events available for both genders
      }
      
      // For other age groups, show all events
      return true;
    });
  };

  // Helper: Count students already registered for this event at this school
  const getEventStudentCount = (eventId: string, gender?: string, excludeFromIndex?: number, ageGroup?: string | null) => {
    let count = 0;
    
    // Count from current form (only up to excludeFromIndex if specified)
    students.forEach((s, idx) => {
      if (s.sports.includes(eventId) && (!gender || s.gender === gender)) {
        // Also check age group if provided
        if (ageGroup) {
          const studentAgeGroup = getEffectiveAgeGroup(s); // Use effective age group for form students
          if (studentAgeGroup !== ageGroup) return;
        }
        
        if (excludeFromIndex === undefined || idx < excludeFromIndex) {
          count++;
        }
      }
    });
    
    // Count from existing registrations - these use stored age_group from database
    let dbCount = 0;
    if (schoolId && registrations) {
      dbCount = registrations.filter(r => 
        r.schoolId === schoolId && 
        r.eventId === eventId &&
        (!gender || r.gender === gender) &&
        (!ageGroup || r.age_group === ageGroup) // Database age_group is already stored correctly
      ).length;
    }
    
    return count + dbCount;
  };

  // Helper: Auto-select available mandatory events for Grade 7 students
  const autoSelectGrade7Events = (student: StudentForm, studentIndex?: number) => {
    if (!isGrade7Student(student) || !student.gender) return [];
    
    const ageGroup = getEffectiveAgeGroup(student);
    // Only add mandatory events that still have spots available for this gender
    const availableMandatory = GRADE_7_MANDATORY_EVENTS.filter(eventId => {
      const currentCount = getEventStudentCount(eventId, student.gender, studentIndex, ageGroup);
      return currentCount < 6;
    });
    
    return availableMandatory;
  };

  // Add/Remove student functions
  const addStudent = () => {
    setStudents(prev => [...prev, { 
      firstName: '', 
      lastName: '', 
      gender: '', 
      dateOfBirth: '', 
      sports: [],
      ageGroup: undefined
    }]);
    setErrors(prev => [...prev, '']);
    setCurrentStep(students.length); // Navigate to the new student
  };

  const removeStudent = (idx: number) => {
    setStudents(prev => prev.filter((_, i) => i !== idx));
    setErrors(prev => prev.filter((_, i) => i !== idx));
  };

  // Handle student field changes
  const handleStudentChange = (idx: number, field: keyof StudentForm, value: any) => {
    setStudents(prev => prev.map((s, i) => {
      if (i !== idx) return s;
      const updated = { ...s, [field]: value };
      
      // Clear sports if age group changed
      if (field === 'dateOfBirth' || field === 'gender') {
        updated.sports = [];
        
        // Auto-add available mandatory events for Grade 7 students (only if both date and gender are set)
        if (getAgeGroup(updated.dateOfBirth) === 'Grade 7' && updated.gender) {
          updated.sports = autoSelectGrade7Events(updated, idx);
        }
      }
      
      return updated;
    }));
    
    // Clear error for this student
    if (errors[idx]) {
      setErrors(prev => prev.map((e, i) => i === idx ? '' : e));
    }
  };

  // Handle sport selection
  const handleSportChange = (idx: number, eventId: string) => {
    setStudents(prev => prev.map((s, i) => {
      if (i !== idx) return s;
      
      const ageGroup = getAgeGroup(s.dateOfBirth);
      
      // For Grade 7 students, prevent removal of mandatory events (but allow relay removal)
      if (isGrade7Student(s) && GRADE_7_MANDATORY_EVENTS.includes(eventId) && s.sports.includes(eventId)) {
        alert('Cannot remove mandatory events for Grade 7 students. Grade 7 must participate in: 100m, 1000m, Javelin Throw, and Long Jump.');
        return s; // Return unchanged
      }
      
      let sports = s.sports.includes(eventId)
        ? s.sports.filter(e => e !== eventId)
        : [...s.sports, eventId];
      
      // For Grade 7 students, allow up to 5 events (4 mandatory + 1 optional relay)
      // For Under 15 students, max 3 sports
      // For other students, max 3 sports
      const maxAllowed = isGrade7Student(s) ? 5 : 3;
      if (sports.length > maxAllowed) {
        const eventName = events.find(e => e.id === eventId)?.name || eventId;
        if (isGrade7Student(s)) {
          alert(`Grade 7 students can participate in up to 5 events (4 mandatory + relay). Cannot add ${eventName}.`);
        } else if (ageGroup === 'Under 15') {
          alert(`Under 15 students can participate in maximum 3 events. Cannot add ${eventName}.`);
        } else {
          alert(`Maximum 3 sports per student. Cannot add ${eventName}.`);
        }
        return s; // Return unchanged
      }
      
      // Check school limit for this sport
      if (sports.includes(eventId)) { // If we're adding this sport
        const isGrade7 = isGrade7Student(s);
        const isUnder15 = ageGroup === 'Under 15';
        const isUnder17 = ageGroup === 'Under 17';
        const isUnder20 = ageGroup === 'Under 20';
        
        // For Grade 7, Under 15, Under 17, and Under 20, filter by both age group and gender; for others, filter by age group only
        const existingCount = registrations.filter(reg => 
          reg.schoolId === schoolId && 
          reg.eventId === eventId && 
          (reg.status === 'approved' || reg.status === 'pending') &&
          reg.age_group === ageGroup &&
          ((!isGrade7 && !isUnder15 && !isUnder17 && !isUnder20) || reg.gender === s.gender)
        ).length;
        
        const currentSubmissionCount = students.filter((student, studentIdx) => {
          const studentAgeGroup = getAgeGroup(student.dateOfBirth);
          const hasEvent = student.sports.includes(eventId) || (studentIdx === idx && sports.includes(eventId));
          return hasEvent && 
                 studentAgeGroup === ageGroup &&
                 ((!isGrade7 && !isUnder15 && !isUnder17 && !isUnder20) || student.gender === s.gender);
        }).length;
        
        // Grade 7 gets 6 students per event per gender, Under 15/17/20 get 3 per event per gender, others get 2 per event per age group
        const maxStudentsForEvent = isGrade7 ? 6 : ((isUnder15 || isUnder17 || isUnder20) ? 3 : 2);
        
        if (existingCount + currentSubmissionCount > maxStudentsForEvent) {
          const eventName = events.find(e => e.id === eventId)?.name || eventId;
          const limit = isGrade7 ? '6' : ((isUnder15 || isUnder17 || isUnder20) ? '3' : '2');
          const genderText = (isGrade7 || isUnder15 || isUnder17 || isUnder20) ? ` (${s.gender})` : '';
          const ageGroupText = ageGroup ? ` for ${ageGroup}` : '';
          alert(`School limit exceeded: Your school already has ${existingCount} student(s) registered/pending for ${eventName}${ageGroupText}${genderText}. Maximum is ${limit} students per school per sport${ageGroupText}${genderText}.`);
          return s; // Return unchanged
        }
      }
      
      return { ...s, sports };
    }));
  };

  // Validation
  const validateAll = () => {
    const newErrors: string[] = students.map((s, idx) => {
      if (!s.firstName.trim()) return 'First name is required.';
      if (!s.lastName.trim()) return 'Last name is required.';
      if (!s.gender) return 'Gender is required.';
      if (!s.dateOfBirth) return 'Date of birth is required.';
      
      // For 13-year-olds, age group must be manually selected
      if (is13YearsOld(s.dateOfBirth) && !s.ageGroup) {
        return 'Please select age group for 13-year-old student (Grade 7 or Under 15).';
      }
      
      const ageGroup = getEffectiveAgeGroup(s);
      if (!ageGroup) return 'Student age must be between 10-20 years.';
      
      if (s.sports.length === 0) return 'Select at least one sport.';
      
      // Grade 7 specific validation
      if (isGrade7Student(s)) {
        const missing = getMissingMandatoryEvents(s);
        if (missing.length > 0) {
          const eventNames = missing.map(id => events.find(e => e.id === id)?.name || id).join(', ');
          return `Grade 7 must participate in available mandatory events. Missing: ${eventNames}`;
        }
        
        // Check if student has any sports at all
        if (s.sports.length === 0) {
          const ageGroup = getEffectiveAgeGroup(s);
          const availableMandatory = GRADE_7_MANDATORY_EVENTS.filter(eventId => {
            const currentCount = getEventStudentCount(eventId, s.gender, undefined, ageGroup);
            return currentCount < 6;
          });
          
          if (availableMandatory.length === 0) {
            // Only relay is available
            return `Grade 7: All mandatory events are full for ${s.gender} students. You can only register for relay team.`;
          } else {
            return 'Grade 7 must participate in at least the available mandatory events.';
          }
        }
        
        // Maximum validation - can participate in up to 5 events (4 mandatory + relay)
        if (s.sports.length > 5) {
          return `Grade 7 can participate in maximum 5 events (4 mandatory + relay).`;
        }
      } else {
        // Non-Grade 7 students
        if (s.sports.length > 3) return 'Maximum 3 sports per student.';
      }
      
      // Check school limit per event
      for (const eventId of s.sports) {
        const ageGroup = getAgeGroup(s.dateOfBirth);
        const isUnder15 = ageGroup === 'Under 15';
        const isUnder17 = ageGroup === 'Under 17';
        const isUnder20 = ageGroup === 'Under 20';
        const maxForEvent = isGrade7Student(s) ? 6 : ((isUnder15 || isUnder17 || isUnder20) ? 3 : 2);
        const gender = (isGrade7Student(s) || isUnder15 || isUnder17 || isUnder20) ? s.gender : undefined;
        if (getEventStudentCount(eventId, gender, undefined, ageGroup) > maxForEvent) {
          const eventName = events.find(e => e.id === eventId)?.name || eventId;
          const limit = isGrade7Student(s) ? '6' : ((isUnder15 || isUnder17 || isUnder20) ? '3' : '2');
          return `Maximum ${limit} students per school for ${eventName}.`;
        }
      }
      
      return '';
    });
    
    // Check Grade 7 participant limits
    const grade7Errors = validateGrade7Rules();
    if (grade7Errors.length > 0) {
      // Add Grade 7 specific errors to the first Grade 7 student or first student
      const firstGrade7Index = students.findIndex(s => isGrade7Student(s));
      const targetIndex = firstGrade7Index >= 0 ? firstGrade7Index : 0;
      if (targetIndex < newErrors.length && !newErrors[targetIndex]) {
        newErrors[targetIndex] = grade7Errors[0];
      }
    }
    
    setErrors(newErrors);
    return newErrors.every(e => !e) && grade7Errors.length === 0;
  };

  // Check registration limits
  const checkRegistrationLimits = async () => {
    const alerts = [];
    
    // Check Grade 7 participant limits
    const grade7Errors = validateGrade7Rules();
    alerts.push(...grade7Errors);
    
    // Check each student
    for (let i = 0; i < students.length; i++) {
      const student = students[i];
      
      // Check sports count based on age group
      const maxSports = isGrade7Student(student) ? 5 : 3;
      if (student.sports.length > maxSports) {
        if (isGrade7Student(student)) {
          alerts.push(`Student ${i + 1} (${student.firstName} ${student.lastName}) - Grade 7 can participate in maximum 5 events (4 mandatory + relay).`);
        } else {
          alerts.push(`Student ${i + 1} (${student.firstName} ${student.lastName}) has ${student.sports.length} sports selected. Maximum is 3 sports per student.`);
        }
      }
      
      // Check mandatory events for Grade 7
      if (isGrade7Student(student)) {
        const missing = getMissingMandatoryEvents(student);
        if (missing.length > 0) {
          const eventNames = missing.map(id => events.find(e => e.id === id)?.name || id).join(', ');
          alerts.push(`Student ${i + 1} (${student.firstName} ${student.lastName}) - Grade 7 missing mandatory events: ${eventNames}`);
        }
      }
      
      // Check each sport for school limit
      for (const eventId of student.sports) {
        try {
          const studentAgeGroup = getAgeGroup(student.dateOfBirth);
          
          // Get existing registrations for this school, event, age group, and gender
          const existingRegistrations = registrations.filter(reg => 
            reg.schoolId === schoolId && 
            reg.eventId === eventId && 
            (reg.status === 'approved' || reg.status === 'pending') &&
            reg.gender === student.gender &&
            reg.age_group === studentAgeGroup
          );
          
          // Count how many students from our current submission would register for this event with same age group and gender
          const currentSubmissionCount = students.filter(s => {
            const sAgeGroup = getAgeGroup(s.dateOfBirth);
            return s.sports.includes(eventId) &&
                   s.gender === student.gender &&
                   sAgeGroup === studentAgeGroup;
          }).length;
          
          // Special limit for Grade 7 - 6 students per event, others get 3 per age/gender combination
          const maxForEvent = isGrade7Student(student) ? 6 : 3;
          
          // Check if total would exceed limit
          if (existingRegistrations.length + currentSubmissionCount > maxForEvent) {
            const event = events.find(e => e.id === eventId);
            const limitText = isGrade7Student(student) ? '6' : '3';
            const categoryText = isGrade7Student(student) 
              ? `${student.gender} Grade 7` 
              : `${student.gender} ${studentAgeGroup}`;
            alerts.push(`Too many students for ${event?.name || eventId}. Your school already has ${existingRegistrations.length} student(s) registered/pending, and you're trying to add ${currentSubmissionCount} more. Maximum is ${limitText} students per school per sport per ${categoryText} category.`);
          }
        } catch (error) {
          console.error('Error checking registrations for event:', eventId, error);
        }
      }
    }
    
    return alerts;
  };

  // Submit all registrations
  const handleSubmit = async () => {
    if (!schoolId) {
      alert('Please select a school.');
      return;
    }
    
    if (!validateAll()) return;
    
    // Check registration limits
    const limitAlerts = await checkRegistrationLimits();
    if (limitAlerts.length > 0) {
      alert(`Registration Limits Exceeded:\n\n${limitAlerts.join('\n\n')}`);
      return;
    }
    
    setIsSubmitting(true);
    try {
      const registrationPromises = [];
      
      for (const student of students) {
        for (const eventId of student.sports) {
          // Calculate age group based on date of birth and manual selection for 13-year-olds
          // Use effective age group calculation
          const ageGroup = getEffectiveAgeGroup(student);
          
          // Create registration entry (one per sport per student)
          const registrationData = {
            school_id: user?.schoolId || schoolId,
            first_name: student.firstName,
            last_name: student.lastName,
            date_of_birth: student.dateOfBirth,
            gender: student.gender,
            sport: eventId,
            age_group: ageGroup,
            status: 'pending',
            created_by: user?.id
          };
          
          registrationPromises.push(
            createRegistration(registrationData)
          );
        }
      }
      
      // Wait for all registrations to complete
      await Promise.all(registrationPromises);
      
      showSuccessToast('Registration Successful!\n\nAll registrations are pending approval by the Headteacher.');
      
      // Navigate to dashboard after a brief delay
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      alert((error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toast functionality
  const showSuccessToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    
    // Auto-hide after 4 seconds
    setTimeout(() => {
      setShowToast(false);
    }, 4000);
  };

  return (
    <>
      <DashboardHeader />
      <div className="registration-container" style={{ paddingTop: '100px' }}>
      <div className="registration-wrapper">
        {!isRegistrationOpen ? (
          <div className="registration-closed">
            <div className="registration-closed-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            <h2>Registration Closed</h2>
            <p>
              Student registration is currently closed. Please contact your administrator for more information.
            </p>
            <button 
              onClick={() => window.history.back()}
              className="action-btn secondary"
            >
              Go Back
            </button>
          </div>
        ) : (
          <React.Fragment>
            <div className="registration-header">
              <h1>Register <span>Student Athletes</span></h1>
              <p>
                Add multiple students and submit all at once. Each student can register for up to 3 sports.
              </p>
          
          {/* Registration Rules Button */}
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <button
              type="button"
              onClick={() => setShowRegistrationRules(true)}
              className="registration-rules-btn"
            >
              <svg 
                width="18" 
                height="18" 
                viewBox="0 0 24 24" 
                fill="currentColor"
              >
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
              </svg>
              View Registration Rules
            </button>
          </div>
        </div>

        <div className="registration-layout">
          {/* Main Form */}
          <div className="registration-form-card">
            <div className="registration-form-body">
          {/* Step Navigation Header */}
          <div className="step-navigation-header">
            <div className="step-navigation-dots">
              {students.map((student, idx) => {
                const isComplete = isStudentComplete(student);
                const isCurrent = idx === currentStep;
                const isVisited = idx < currentStep;
                
                return (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    <div 
                      className={`step-dot ${isCurrent ? 'current' : isComplete ? 'complete' : isVisited ? 'visited' : 'incomplete'}`}
                      onClick={() => setCurrentStep(idx)}
                      title={`Student ${idx + 1}: ${student.firstName || 'Unnamed'} ${student.lastName || ''} ${isComplete ? '(Complete)' : isCurrent ? '(Current)' : '(Incomplete)'}`}
                    >
                      {isComplete && !isCurrent ? '✓' : idx + 1}
                    </div>
                    {idx < students.length - 1 && (
                      <div className={`step-connector ${(isComplete || isVisited) ? 'active' : ''}`} />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="step-controls">
              <p>
                Student {currentStep + 1} of {students.length}
              </p>
              {students.length > 10 && (
                <div style={{ fontSize: '12px', color: 'var(--reg-warning)', fontWeight: '500' }}>
                  ⚠️ Large submission ({students.length} students)
                </div>
              )}
            </div>
            
            {students.length > 20 && (
              <div className="info-box warning" style={{ marginTop: '1rem', marginBottom: 0 }}>
                <span className="info-icon">💡</span>
                <span className="info-text">Consider submitting in smaller batches for better performance</span>
              </div>
            )}
          </div>

          {/* Current Student Form */}
          {students.length > 0 && (
            <div className="student-entry">
              <div className="student-entry-header">
                <h3>
                  Student {currentStep + 1} 
                  {students[currentStep]?.firstName && ` — ${students[currentStep].firstName} ${students[currentStep].lastName}`}
                </h3>
                {students.length > 1 && (
                  <button 
                    type="button" 
                    className="btn-danger btn-small" 
                    onClick={() => {
                      removeStudent(currentStep);
                      if (currentStep >= students.length - 1 && currentStep > 0) {
                        setCurrentStep(currentStep - 1);
                      }
                    }}
                  >
                    Remove Student
                  </button>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input 
                    type="text" 
                    placeholder="Enter first name"
                    value={students[currentStep]?.firstName || ''} 
                    onChange={e => handleStudentChange(currentStep, 'firstName', e.target.value)}
                  />
                </div>
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input 
                    type="text" 
                    placeholder="Enter last name"
                    value={students[currentStep]?.lastName || ''} 
                    onChange={e => handleStudentChange(currentStep, 'lastName', e.target.value)}
                  />
                </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Gender *</label>
                  <select 
                    value={students[currentStep]?.gender || ''} 
                    onChange={e => handleStudentChange(currentStep, 'gender', e.target.value)}
                  >
                    <option value="">Select gender...</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date of Birth *</label>
                  <input 
                    type="date" 
                    value={students[currentStep]?.dateOfBirth || ''} 
                    onChange={e => handleStudentChange(currentStep, 'dateOfBirth', e.target.value)}
                  />
                  {students[currentStep]?.dateOfBirth && (
                    <div style={{ marginTop: '0.75rem' }}>
                      {is13YearsOld(students[currentStep].dateOfBirth) ? (
                        <div className="form-group" style={{ marginTop: '0' }}>
                          <label style={{ 
                            color: 'var(--reg-warning)', 
                            marginBottom: '0.5rem',
                            fontSize: '13px'
                          }}>
                            This student is 13 years old — Please select age group: *
                          </label>
                          <select
                            value={students[currentStep].ageGroup || ''}
                            onChange={(e) => {
                              const newStudents = [...students];
                              const updatedStudent = {
                                ...newStudents[currentStep],
                                ageGroup: e.target.value,
                                sports: [] as string[] // Reset sports when age group changes
                              };
                              
                              // Auto-select mandatory events if Grade 7 is selected
                              if (e.target.value === 'Grade 7' && updatedStudent.gender) {
                                updatedStudent.sports = autoSelectGrade7Events(updatedStudent, currentStep);
                              }
                              
                              newStudents[currentStep] = updatedStudent;
                              setStudents(newStudents);
                              
                              // Clear validation error for this student
                              const newErrors = [...errors];
                              newErrors[currentStep] = '';
                              setErrors(newErrors);
                            }}
                          >
                            <option value="">Select Age Group...</option>
                            <option value="Grade 7">Grade 7</option>
                            <option value="Under 15">Under 15</option>
                          </select>
                          {students[currentStep].ageGroup && (
                            <div style={{ 
                              marginTop: '0.5rem', 
                              fontSize: '12px', 
                              color: 'var(--reg-success)',
                              fontWeight: '500'
                            }}>
                              ✓ Selected: {students[currentStep].ageGroup}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div style={{ 
                          color: 'var(--reg-success)', 
                          fontWeight: '500',
                          fontSize: '13px'
                        }}>
                          Age Group: {getEffectiveAgeGroup(students[currentStep]) || 'Not eligible (age 10-20 only)'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Sports Selection */}
              {students[currentStep]?.dateOfBirth && students[currentStep]?.gender && 
               ((!is13YearsOld(students[currentStep].dateOfBirth) && getAgeGroup(students[currentStep].dateOfBirth)) ||
                (is13YearsOld(students[currentStep].dateOfBirth) && students[currentStep].ageGroup)) && (
                <div className="sports-selection">
                  {isGrade7Student(students[currentStep]) ? (
                    <div>
                      <div className="sports-category-header">
                        <span className="sports-category-icon" style={{ background: 'linear-gradient(135deg, var(--reg-accent) 0%, var(--reg-accent-hover) 100%)' }}>G7</span>
                        Grade 7 Events
                      </div>
                      <div className="info-box warning">
                        {(() => {
                          const ageGroup = getEffectiveAgeGroup(students[currentStep]);
                          const availableMandatory = GRADE_7_MANDATORY_EVENTS.filter(eventId => {
                            const currentCount = getEventStudentCount(eventId, students[currentStep].gender, currentStep, ageGroup);
                            return currentCount < 6;
                          });
                          
                          if (availableMandatory.length > 0) {
                            const eventNames = availableMandatory.map(id => events.find(e => e.id === id)?.name || id).join(', ');
                            return (
                              <>
                                <span className="info-icon">⚠️</span>
                                <div className="info-text">
                                  <strong>Mandatory Events:</strong> {eventNames}
                                  <div style={{ marginTop: '6px', opacity: 0.8, fontSize: '12px' }}>
                                    + 4×100m Relay is optional (separate relay teams allowed)
                                  </div>
                                </div>
                              </>
                            );
                          } else {
                            const gender = students[currentStep].gender;
                            return (
                              <>
                                <span className="info-icon">ℹ️</span>
                                <span className="info-text">
                                  All mandatory events are full for {gender} students (6/6 each). You can only register for relay team.
                                </span>
                              </>
                            );
                          }
                        })()}
                      </div>
                    </div>
                  ) : (
                    <div className="sports-category-header">
                      <span className="sports-category-icon">📋</span>
                      Select Sports (max 3) — Selected: {students[currentStep]?.sports.length || 0}/3
                    </div>
                  )}
                  <div>
                    {/* Track Events */}
                    <div style={{ marginBottom: '28px' }}>
                      <h3 className="sports-category-header">
                        <span className="sports-category-icon">T</span>
                        Track Events
                      </h3>
                      <div className="sports-grid">
                        {getEligibleSports(students[currentStep]).filter(event => event.category === 'Track').map(event => {
                      const isSelected = students[currentStep].sports.includes(event.id);
                      const isGrade7 = isGrade7Student(students[currentStep]);
                      const ageGroup = getEffectiveAgeGroup(students[currentStep]);
                      const isUnder15 = ageGroup === 'Under 15';
                      const isUnder17 = ageGroup === 'Under 17';
                      const isUnder20 = ageGroup === 'Under 20';
                      const isMandatory = isGrade7 && GRADE_7_MANDATORY_EVENTS.includes(event.id);
                      
                      // Calculate max sports for this student
                      const maxSports = isGrade7 ? (GRADE_7_MANDATORY_EVENTS.length + 1) : 3; // Grade 7: 4 mandatory + 1 optional relay = 5 max
                      
                      // Count current registrations excluding this student (for availability check)
                      // For all age groups, filter by both age group and gender
                      const currentRegistrations = getEventStudentCount(event.id, students[currentStep].gender, currentStep, ageGroup);
                      const maxStudents = isGrade7 ? 6 : ((isUnder15 || isUnder17 || isUnder20) ? 3 : 2);
                      
                      const isDisabled = !isSelected && (
                        students[currentStep].sports.length >= maxSports ||
                        currentRegistrations >= maxStudents
                      );
                      
                      // For display, show total count including all current form students
                      const studentCount = getEventStudentCount(event.id, students[currentStep].gender, undefined, ageGroup);
                      
                      return (
                        <label 
                          key={event.id} 
                          className={`sport-option ${isSelected ? 'selected' : ''} ${isMandatory ? 'mandatory' : ''} ${isDisabled && !isMandatory ? 'disabled' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isMandatory || isDisabled}
                            onChange={() => handleSportChange(currentStep, event.id)}
                          />
                          <div className="sport-option-content">
                            <div className="sport-name">
                              {event.name}
                              {isMandatory && (
                                <span className="sport-badge required">Required</span>
                              )}
                              {isGrade7 && event.id === '4x100_relay' && (
                                <span className="sport-badge optional">Optional</span>
                              )}
                            </div>
                            <div className="sport-category">{event.category}</div>
                            <div className="sport-count">
                              Students: {studentCount}/{maxStudents}
                              {isGrade7 && ` (${students[currentStep].gender}: up to 6)`}
                              {(isUnder15 || isUnder17 || isUnder20) && ` (${ageGroup} ${students[currentStep].gender}: up to 3)`}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                      </div>
                    </div>

                    {/* Field Events */}
                    <div>
                      <h3 className="sports-category-header">
                        <span className="sports-category-icon field">F</span>
                        Field Events
                      </h3>
                      <div className="sports-grid">
                        {getEligibleSports(students[currentStep]).filter(event => event.category === 'Field').map(event => {
                          const isSelected = students[currentStep].sports.includes(event.id);
                          const isGrade7 = isGrade7Student(students[currentStep]);
                          const ageGroup = getEffectiveAgeGroup(students[currentStep]);
                          const isUnder15 = ageGroup === 'Under 15';
                          const isUnder17 = ageGroup === 'Under 17';
                          const isUnder20 = ageGroup === 'Under 20';
                          const isMandatory = isGrade7 && GRADE_7_MANDATORY_EVENTS.includes(event.id);
                          
                          // Calculate max sports for this student
                          const maxSports = isGrade7 ? (GRADE_7_MANDATORY_EVENTS.length + 1) : 3; // Grade 7: 4 mandatory + 1 optional relay = 5 max
                          
                          // Count current registrations excluding this student (for availability check)
                          // For all age groups, filter by both age group and gender
                          const currentRegistrations = getEventStudentCount(event.id, students[currentStep].gender, currentStep, ageGroup);
                          const maxStudents = isGrade7 ? 6 : ((isUnder15 || isUnder17 || isUnder20) ? 3 : 2);
                          
                          const isDisabled = !isSelected && (
                            students[currentStep].sports.length >= maxSports ||
                            currentRegistrations >= maxStudents
                          );
                          
                          // For display, show total count including all current form students
                          const studentCount = getEventStudentCount(event.id, students[currentStep].gender, undefined, ageGroup);
                          
                          return (
                            <label 
                              key={event.id} 
                              className={`sport-option ${isSelected ? 'selected' : ''} ${isMandatory ? 'mandatory' : ''} ${isDisabled && !isMandatory ? 'disabled' : ''}`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                disabled={isMandatory || isDisabled}
                                onChange={() => handleSportChange(currentStep, event.id)}
                              />
                              <div className="sport-option-content">
                                <div className="sport-name">
                                  {event.name}
                                  {isMandatory && (
                                    <span className="sport-badge required">Required</span>
                                  )}
                                  {isGrade7 && event.id === '4x100_relay' && (
                                    <span className="sport-badge optional">Optional</span>
                                  )}
                                </div>
                                <div className="sport-category">{event.category}</div>
                                <div className="sport-count">
                                  Students: {studentCount}/{maxStudents}
                                  {isGrade7 && ` (${students[currentStep].gender}: up to 6)`}
                                  {(isUnder15 || isUnder17 || isUnder20) && ` (${ageGroup} ${students[currentStep].gender}: up to 3)`}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {errors[currentStep] && (
                <div className="form-error">
                  {errors[currentStep]}
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="action-buttons" style={{ marginTop: '1.5rem', paddingTop: '1.5rem' }}>
            <div>
              {currentStep > 0 && (
                <button 
                  type="button" 
                  className="action-btn secondary" 
                  onClick={() => setCurrentStep(currentStep - 1)}
                >
                  ← Previous Student
                </button>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              {currentStep < students.length - 1 ? (
                <button 
                  type="button" 
                  className="action-btn primary" 
                  onClick={() => setCurrentStep(currentStep + 1)}
                >
                  Next Student →
                </button>
              ) : (
                <button 
                  type="button" 
                  className="add-student-btn" 
                  onClick={addStudent}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add Another Student
                </button>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <button 
              className="action-btn primary" 
              onClick={handleSubmit} 
              disabled={isSubmitting || !schoolId || students.length === 0}
              style={{ minWidth: '250px' }}
            >
              {isSubmitting ? (
                <>
                  <span className="loading-spinner" />
                  Submitting...
                </>
              ) : (
                `Submit ${students.length} Student(s)`
              )}
            </button>
          </div>
        </div>
        </div>
      </div>
      
      {/* Success Toast */}
      {showToast && (
        <div className="success-toast">
          <div className="toast-content">
            <span className="toast-icon">✓</span>
            <div className="toast-text">
              {toastMessage}
            </div>
            <button
              className="toast-close"
              onClick={() => setShowToast(false)}
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      {/* Registration Rules Popup Modal */}
      {showRegistrationRules && (
        <div
          className="registration-rules-overlay"
          onClick={() => setShowRegistrationRules(false)}
        >
          <div
            className="registration-rules-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="registration-rules-modal-header">
              <h2>Registration Rules</h2>
              <button
                className="registration-rules-modal-close"
                onClick={() => setShowRegistrationRules(false)}
              >
                ×
              </button>
            </div>
            
            <div className="registration-rules-modal-body">
              <div className="rules-section">
                <h4>Age Groups (as of Dec 31):</h4>
                <ul className="rules-list">
                  <li><strong>Grade 7:</strong> 10 – 12 years</li>
                  <li><strong>Under 15:</strong> 13 - 15 years</li>
                  <li><strong>Under 17:</strong> 16 - 17 years</li>
                  <li><strong>Under 20:</strong> 18 - 20 years</li>
                </ul>
              </div>

              <div className="rules-section">
                <h4>General Limits:</h4>
                <ul className="rules-list">
                  <li>Maximum <strong>3 sports</strong> per student (non-Grade 7)</li>
                  <li>Maximum <strong>3 students</strong> per school per sport per age/gender category</li>
                  <li><strong>Grade 7: 6 students</strong> per school per sport</li>
                  <li>Registrations subject to <strong>Headteacher approval</strong></li>
                </ul>
              </div>

              <div className="rules-section">
                <h4>Grade 7 Special Rules:</h4>
                <ul className="rules-list">
                  <li>Must participate in <strong>ALL mandatory events</strong></li>
                  <li>Events: 100m, 1000m, Javelin, Long Jump</li>
                  <li>4×100m Relay is <strong>optional</strong></li>
                  <li>Maximum <strong>6 boys + 6 girls</strong> per school</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
        </React.Fragment>
        )}
      </div>
      </div>
    </>
  );
};

export default Registration;
